import type { ReactNode } from 'react';
import { Chip, Panel } from '../../kit';
import { useLocale, useT } from '../../../messages';
import type { CatalogItem } from '../../../wire';
import { money, priceText, textOf } from './text';

/**
 * A room type or a trip — **read-only** (`docs/verticals-plan.md` V4).
 *
 * What the seed made, in facts: the pool or the vessel (by id — 0050 fences
 * `resources` to onboarding including `SELECT`, so a name would abort a live
 * business's read), what it sleeps or seats, what a night costs at the
 * calendar's floor, how many starts a week, the season. No box to type in: the
 * catalog file is the door, and a box here that let an operator retype a
 * nightly rate would be a second place the calendar could be decided.
 */
export function VerticalDetail({
  item,
  locale,
  currency,
  satellites,
}: {
  item: CatalogItem;
  locale: string;
  currency: string;
  /** Whether the console's role could read room and sailing details at all. */
  satellites: boolean;
}): ReactNode {
  const t = useT();
  const uiLocale = useLocale();
  const stay = item.stay;
  const trip = item.departure;
  const facts: string[] = [];

  if (stay !== null) {
    facts.push(
      t('catalog.vertical.pool', { id: stay.resource_id }),
      t('catalog.vertical.sleeps', { n: stay.max_occupancy }),
      t('catalog.vertical.nightly', { price: money(stay.base_nightly_cents) }),
      t('catalog.vertical.calendar', { bands: stay.bands, exceptions: stay.exceptions }),
      t('catalog.vertical.minNights', { n: stay.min_stay_nights }),
      t('catalog.vertical.checkTimes', { inAt: stay.check_in, outAt: stay.check_out }),
    );
    if (!stay.bookable_by_partner) facts.push(t('catalog.vertical.offPartner'));
    if (!stay.bookable_online) facts.push(t('catalog.vertical.offSite'));
  }
  if (trip !== null) {
    const starts = trip.schedule.reduce((n, day) => n + day.departures.length, 0);
    facts.push(
      t('catalog.vertical.vessel', { id: trip.resource_id }),
      trip.private
        ? t('catalog.vertical.charter', { n: trip.seats })
        : t('catalog.vertical.seats', { n: trip.seats }),
      t('catalog.vertical.length', { min: trip.duration_min }),
      t('catalog.vertical.starts', { n: starts }),
      trip.season === null
        ? t('catalog.vertical.yearRound')
        : t('catalog.vertical.season', { from: trip.season.from, to: trip.season.to }),
    );
    if (!trip.bookable_by_partner) facts.push(t('catalog.vertical.offPartner'));
    if (!trip.bookable_online) facts.push(t('catalog.vertical.offSite'));
  }

  // "Certified diver · Snorkel rider −70.00 (86’d)": the delta is signed the
  // way the column is, and the board's own state rides along.
  const tiers = (trip?.tiers ?? []).map((tier) => {
    const delta =
      tier.price_delta_cents === 0
        ? ''
        : ` ${tier.price_delta_cents > 0 ? '+' : '−'}${money(Math.abs(tier.price_delta_cents))}`;
    const soldOut = tier.sold_out ? ` (${t('catalog.vertical.soldOut')})` : '';
    return `${textOf(tier.name, locale)}${delta}${soldOut}`;
  });

  return (
    <Panel
      className="catalog-editor"
      title={textOf(item.name, locale) || item.item_id}
      actions={
        item.active ? (
          <Chip tone="ok">{t('catalog.editor.onSale')}</Chip>
        ) : (
          <Chip>{t('catalog.editor.notOnSale')}</Chip>
        )
      }
    >
      <div data-item={item.item_id} className="catalog-editor__body">
        <p className="catalog-fence catalog-fence--open">{t('catalog.vertical.fileIsTheDoor')}</p>
        <p>
          <strong>{priceText(item.price_cents, currency, uiLocale)}</strong>
        </p>
        {!satellites && stay === null && trip === null ? (
          <p className="catalog-fence-note">{t('catalog.vertical.noDetails')}</p>
        ) : null}
        {facts.length > 0 ? (
          <ul className="catalog-facts">
            {facts.map((fact) => (
              <li key={fact}>{fact}</li>
            ))}
          </ul>
        ) : null}
        {tiers.length > 0 ? (
          <p className="muted">
            {t('catalog.vertical.tiers')}: {tiers.join(' · ')}
          </p>
        ) : null}
      </div>
    </Panel>
  );
}
